/**
 * Process-wide singleton for the notifications SignalR connection.
 *
 * Why a module-level singleton instead of a connection per hook instance:
 * the add-in mounts its tree more than once (StrictMode double-invoke + the
 * host bootstrapping remounting TaskPane), so a per-effect connection produces
 * several sockets where only one wins the race to connect — and that winner may
 * belong to an unmounted instance, so its frames update dead React state while
 * the visible instance has no live socket. One shared connection that every
 * hook instance subscribes to removes that whole class of bug.
 *
 * The connection is started lazily on first subscribe and kept alive across
 * remounts (add-in panes churn constantly). It never gives up on a dead hub,
 * but every retry path is on exponential backoff and stops entirely once
 * nothing is subscribed — a hub that cannot connect must not turn the pane into
 * a negotiate loop against the gateway.
 */
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import { config } from "../../shared/config/environment";
import { logModuleFailure, logModuleSuccess } from "../../telemetry";
import type { HubSignal, Notification } from "../types";

type FrameHandler = (n: Notification) => void;
type StateHandler = (connected: boolean) => void;

/** Delay before the first retry of a failing run; each further failure doubles it. */
const RETRY_BASE_MS = 5_000;
/** Backoff ceiling — a hub that is down for good is re-tried this often, no faster. */
const RETRY_CEILING_MS = 300_000;
/** A connection must hold this long before it counts as healthy (clears the backoff). */
const HEALTHY_AFTER_MS = 30_000;
/** After the first failure of a run, log one attempt in every N. */
const FAILURE_LOG_EVERY = 10;
/** Cap on how long a token fetch may block a connect attempt. */
const TOKEN_TIMEOUT_MS = 15_000;

const frameSubs = new Set<FrameHandler>();
const stateSubs = new Set<StateHandler>();
const signalSubs = new Set<(signal: HubSignal) => void>();

let connection: HubConnection | null = null;
let restartTimer: number | null = null;
let healthyTimer: number | null = null;
/** Guards re-entry while a start attempt is awaiting its token. */
let starting = false;
/** Consecutive failed connect attempts; drives every retry delay. */
let consecutiveFailures = 0;
/** Bearer resolved by the in-flight start(), reused by accessTokenFactory. */
let attemptToken: string | null = null;
let tokenProvider: () => Promise<string> = () => Promise.resolve("");

/** Set/replace how the hub fetches a fresh bearer token on (re)connect. */
export function setTokenProvider(fn: () => Promise<string>): void {
  tokenProvider = fn;
}

function emitState(connected: boolean): void {
  stateSubs.forEach((cb) => cb(connected));
}

/**
 * Jitter fraction in [0, 1). Drawn from the Web Crypto RNG rather than
 * Math.random: the value only decorrelates retries and carries no security
 * weight, but Sonar reports Math.random as a security risk (typescript:S2245)
 * and its TS analyzer does not reliably honour an inline suppression — see the
 * note in studio-app's Combobox. A CSPRNG keeps the resolution in app code
 * instead of a scanner directive, at no meaningful cost on a retry path.
 */
function randomFraction(): number {
  const [value] = globalThis.crypto.getRandomValues(new Uint32Array(1));
  return (value ?? 0) / 2 ** 32;
}

/**
 * Exponential backoff with ±25% jitter. `failures` is the count of consecutive
 * failed attempts, so the first retry of a run waits `RETRY_BASE_MS` and a
 * connection that has proven healthy (count back at 0) retries immediately.
 * The jitter keeps several panes/hosts from retrying in lockstep.
 */
function retryDelayMs(failures: number): number {
  if (failures <= 0) {
    return 0;
  }
  const step = Math.min(RETRY_BASE_MS * 2 ** (failures - 1), RETRY_CEILING_MS);
  return Math.round(step * (0.75 + randomFraction() * 0.5));
}

function clearHealthyTimer(): void {
  if (healthyTimer !== null) {
    globalThis.clearTimeout(healthyTimer);
    healthyTimer = null;
  }
}

function cancelRestart(): void {
  if (restartTimer !== null) {
    globalThis.clearTimeout(restartTimer);
    restartTimer = null;
  }
}

function recordFailure(): void {
  clearHealthyTimer();
  consecutiveFailures += 1;
}

function markConnected(): void {
  emitState(true);
  // Clear the backoff only once the socket has PROVEN it can hold. A hub that
  // accepts negotiate and then drops straight back would otherwise reset the
  // count on every cycle, so the delay never grows and the pane negotiates in a
  // tight loop for as long as it stays open.
  clearHealthyTimer();
  healthyTimer = globalThis.setTimeout(() => {
    healthyTimer = null;
    consecutiveFailures = 0;
  }, HEALTHY_AFTER_MS);
}

/**
 * A hub that can never connect emitted one failure event per attempt, which is
 * a request storm of its own on top of the negotiate. Log the first failure of
 * a run and then one in every {@link FAILURE_LOG_EVERY}, so a persistently-dead
 * hub stays visible without flooding telemetry.
 */
function shouldLogFailure(): boolean {
  return consecutiveFailures <= 1 || consecutiveFailures % FAILURE_LOG_EVERY === 0;
}

/**
 * Report a connection we had (or were about to have) and lost.
 *
 * SignalR logs the whole drop/reconnect lifecycle at Information, which
 * `configureLogging(LogLevel.Warning)` discards — so a hub that connects and
 * then drops on a loop is silent in the console AND emits nothing but
 * `connector.action.invoke` successes, reading as 100% healthy while the bell
 * delivers nothing. `err` carries SignalR's own reason when it has one, which is
 * the only place the cause of a clean server-side close survives.
 */
function logHubFailure(err: unknown, reason: string): void {
  if (shouldLogFailure()) {
    logModuleFailure("notifications", err, reason);
  }
}

/**
 * Resolve a bearer for one connect attempt, bounded. `tokenProvider` is an MSAL
 * silent acquisition and can stall indefinitely on a bad network; because
 * `starting` gates every retry path, a fetch that never settles would wedge the
 * hub for the life of the pane. Timing out yields "" — treated as a failed
 * attempt, so the normal backoff retries it.
 */
async function resolveToken(): Promise<string> {
  let timer: number | undefined;
  try {
    return await Promise.race([
      tokenProvider(),
      new Promise<string>((resolve) => {
        timer = globalThis.setTimeout(() => resolve(""), TOKEN_TIMEOUT_MS);
      }),
    ]);
  } finally {
    globalThis.clearTimeout(timer);
  }
}

function scheduleStart(): void {
  if (restartTimer !== null) {
    return;
  }
  // Nothing is subscribed (pane closed or unmounted), so a reconnect has nobody
  // to deliver to. Leave the loop stopped; subscribeToHub picks it back up.
  if (frameSubs.size === 0) {
    return;
  }
  restartTimer = globalThis.setTimeout(() => {
    restartTimer = null;
    void start();
  }, retryDelayMs(consecutiveFailures));
}

function build(): HubConnection {
  // Gateway authorization is JWT-only; the negotiate POST carries the bearer
  // via accessTokenFactory and sends no APIM subscription key. The subsequent
  // WebSocket connects directly to the Azure SignalR Service (default mode),
  // bypassing APIM.
  const conn = new HubConnectionBuilder()
    .withUrl(`${config.addinApiBaseUrl}/hubs/notifications`, {
      // start() has already resolved a bearer for this attempt — reuse it so a
      // single connect doesn't trigger two silent token acquisitions. Automatic
      // reconnects don't run through start(), so they resolve their own.
      accessTokenFactory: () => (attemptToken ? Promise.resolve(attemptToken) : tokenProvider()),
    })
    .withAutomaticReconnect({
      // Never give up, but derive the delay from the shared failure count as
      // well as ctx.previousRetryCount: previousRetryCount resets on every
      // successful reconnect, so a socket that reconnects and immediately drops
      // again would otherwise retry at 0ms indefinitely.
      nextRetryDelayInMilliseconds: (ctx) => retryDelayMs(consecutiveFailures + ctx.previousRetryCount),
    })
    .configureLogging(LogLevel.Warning)
    .build();

  conn.on("notification", (n: Notification) => {
    frameSubs.forEach((handler) => handler(n));
  });
  conn.on("signal", (signal: HubSignal) => {
    signalSubs.forEach((handler) => handler(signal));
  });
  conn.onreconnected(() => markConnected());
  conn.onreconnecting((err) => {
    // A drop inside the probation window is a flapping connection, not a proven
    // one losing its socket — count it so the backoff grows. A drop after
    // probation leaves the count at 0, so SignalR still retries immediately.
    if (healthyTimer !== null) {
      recordFailure();
    }
    logHubFailure(err ?? new Error("hub connection dropped"), "hub-dropped");
    emitState(false);
  });
  conn.onclose((err) => {
    recordFailure();
    logHubFailure(err ?? new Error("hub connection closed"), "hub-closed");
    emitState(false);
    scheduleStart();
  });
  return conn;
}

async function start(): Promise<void> {
  if (!config.addinApiBaseUrl || starting) {
    return;
  }
  connection ??= build();
  // start() is only valid from Disconnected; a reconnect/concurrent start may
  // already have it Connecting/Connected.
  if (connection.state !== HubConnectionState.Disconnected) {
    return;
  }

  starting = true;
  try {
    // Negotiating without a bearer is a guaranteed rejection, and the pane
    // mounts before auth settles — attempting it only feeds the retry loop.
    attemptToken = await resolveToken();
    if (!attemptToken) {
      recordFailure();
      emitState(false);
      scheduleStart();
      return;
    }
    await connection.start();
    markConnected();
    logModuleSuccess("notifications", "hub-start");
  } catch (err) {
    // SignalR connects outside fetchWithRefresh, so a hub that can never connect
    // was fully silent — the bell just showed "disconnected" with no telemetry.
    recordFailure();
    logHubFailure(err, "hub-start-failed");
    emitState(false);
    scheduleStart();
  } finally {
    attemptToken = null;
    starting = false;
  }
}

/** True once the shared connection is established. */
export function isHubConnected(): boolean {
  return connection?.state === HubConnectionState.Connected;
}

/**
 * Subscribe to live notification frames and connection-state changes. Starts
 * the shared connection if it isn't already running. Returns an unsubscribe
 * function; an established connection is intentionally left alive after the
 * last unsubscribe so frequent add-in remounts don't thrash it.
 */
/**
 * Listen for status-only signals. They ride the connection that `subscribeToHub`
 * keeps open, so this never starts or holds one itself.
 */
export function subscribeToSignals(onSignal: (signal: HubSignal) => void): () => void {
  signalSubs.add(onSignal);
  return () => {
    signalSubs.delete(onSignal);
  };
}

export function subscribeToHub(onFrame: FrameHandler, onState: StateHandler): () => void {
  frameSubs.add(onFrame);
  stateSubs.add(onState);
  // Reflect current state immediately so a late subscriber isn't stuck "off".
  onState(isHubConnected());
  // A queued retry means the hub is mid-failure: let the backoff run instead of
  // negotiating again on every remount, which would bypass it entirely.
  if (restartTimer === null) {
    void start();
  }

  return () => {
    frameSubs.delete(onFrame);
    stateSubs.delete(onState);
    if (frameSubs.size === 0) {
      // Nobody left to deliver to — drop the queued retry rather than
      // negotiating against a dead hub for the life of the host process.
      cancelRestart();
    }
  };
}
